import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { useP2POrder, useP2PChat } from '../../hooks/useP2P';
import { formatNumber } from '../../utils/formatters';
import P2PChat from './P2PChat';

const P2POrderDetail = () => {
  const { orderId } = useParams();
  const [showChat, setShowChat] = useState(true); // Open by default
  const [paymentReference, setPaymentReference] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const {
    order,
    loading,
    error,
    markPaid,
    confirmPayment,
    releaseCrypto,
    cancelOrder
  } = useP2POrder(orderId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-white">Loading order details...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">
            {error || 'Order not found'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-white';
      case 'paid': return 'text-blue-400';
      case 'escrow': return 'text-purple-400';
      case 'completed': return 'text-green-400';
      case 'cancelled': return 'text-red-400';
      case 'disputed': return 'text-orange-400';
      default: return 'text-white';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Waiting for Payment';
      case 'paid': return 'Payment Made';
      case 'escrow': return 'In Escrow';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      case 'disputed': return 'Disputed';
      default: return status;
    }
  };

  const handleMarkPaid = async () => {
    try {
      await markPaid(paymentReference);
      setPaymentReference('');
    } catch (error) {
      console.error('Failed to mark as paid:', error);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      await confirmPayment();
    } catch (error) {
      console.error('Failed to confirm payment:', error);
    }
  };

  const handleReleaseCrypto = async () => {
    try {
      await releaseCrypto();
    } catch (error) {
      console.error('Failed to release crypto:', error);
    }
  };

  const handleCancelOrder = async () => {
    try {
      await cancelOrder(cancellationReason);
      setShowCancelModal(false);
      setCancellationReason('');
    } catch (error) {
      console.error('Failed to cancel order:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">P2P Order</h1>
            <div className={`flex items-center space-x-2 ${getStatusColor(order.status)}`}>
              <div className="w-3 h-3 rounded-full bg-current"></div>
              <span className="font-medium">{getStatusText(order.status)}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 text-white">
            <span>Order ID:</span>
            <span className="font-mono">{order.order_id}</span>
            <button
              onClick={() => copyToClipboard(order.order_id)}
              className="text-blue-400 hover:text-blue-300"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Order Details */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-semibold mb-4">Order Details</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-white">Type</span>
                  <p className="font-medium">{order.ad.type === 'buy' ? 'Buy' : 'Sell'} {order.ad.asset}</p>
                </div>
                <div>
                  <span className="text-white">Amount</span>
                  <p className="font-medium">{formatNumber(order.crypto_amount)} {order.ad.asset}</p>
                </div>
                <div>
                  <span className="text-white">Price</span>
                  <p className="font-medium">{formatNumber(order.price)} {order.ad.fiat_currency}</p>
                </div>
                <div>
                  <span className="text-white">Total</span>
                  <p className="font-medium">{formatNumber(order.fiat_amount)} {order.ad.fiat_currency}</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="mb-6">
                <span className="text-white">Payment Method</span>
                <p className="font-medium">{order.payment_method.name}</p>
              </div>

              {/* Payment Account Details */}
              {order.payment_account_info && Object.keys(order.payment_account_info).length > 0 && (
                <div className="mb-6 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-600/30 rounded-xl p-5">
                  <h3 className="font-semibold text-blue-400 mb-4 text-lg flex items-center space-x-2">
                    <ShieldCheckIcon className="h-5 w-5" />
                    <span>Payment Account Details</span>
                  </h3>
                  <div className="space-y-3">
                    {order.payment_account_info.details ? (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <pre className="text-white font-mono text-sm whitespace-pre-wrap break-words">
                          {order.payment_account_info.details}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(order.payment_account_info.details)}
                          className="mt-3 text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-2"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          <span>Copy Details</span>
                        </button>
                      </div>
                    ) : (
                      Object.entries(order.payment_account_info).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-white capitalize">{key.replace(/_/g, ' ')}:</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-medium text-white">{value}</span>
                            <button
                              onClick={() => copyToClipboard(value)}
                              className="text-blue-400 hover:text-blue-300 p-1"
                              title="Copy to clipboard"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {order.can_mark_paid && (
                    <p className="mt-4 text-sm text-white italic">
                      ⚠️ Please make the payment to the above account and click "Mark as Paid" after completing the transfer.
                    </p>
                  )}
                </div>
              )}

              {/* Time Information */}
              {order.status === 'pending' && order.time_remaining_minutes > 0 && (
                <div className="bg-yellow-900/20 border border-[#2A356D]/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-white" />
                    <span className="text-white font-medium">
                      Payment deadline: {order.time_remaining_minutes} minutes remaining
                    </span>
                  </div>
                </div>
              )}

              {/* Payment Reference */}
              {order.payment_reference && (
                <div className="mb-6">
                  <span className="text-white">Payment Reference</span>
                  <p className="font-medium font-mono">{order.payment_reference}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {order.can_mark_paid && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Payment reference (optional)"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                    />
                    <button
                      onClick={handleMarkPaid}
                      className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Mark as Paid
                    </button>
                  </div>
                )}

                {order.can_confirm_payment && (
                  <button
                    onClick={handleConfirmPayment}
                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Confirm Payment Received
                  </button>
                )}

                {order.can_release && (
                  <button
                    onClick={handleReleaseCrypto}
                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Release Crypto
                  </button>
                )}

                {order.can_cancel && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Cancel Order
                  </button>
                )}
              </div>
            </div>

            {/* Trading Partner */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-semibold mb-4">Trading Partner</h2>
              
              {/* Show buyer info if user is seller, and vice versa */}
              {order.buyer_profile && order.seller_profile && (
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {/* Show the other party's initial */}
                      {/* This would need to be determined based on current user */}
                      {order.buyer_profile.nickname.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium">{order.buyer_profile.nickname}</span>
                      {order.buyer_profile.is_verified && (
                        <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-white">
                      <span>Rating: {order.buyer_profile.rating.toFixed(1)}/5</span>
                      <span>Trades: {order.buyer_profile.total_trades}</span>
                      <span>Completion: {order.buyer_profile.completion_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chat Section - Now Full Width */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <div className="bg-[#010615] rounded-xl border border-gray-700 overflow-hidden">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border-b border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Order Chat</h3>
                      <p className="text-sm text-white">
                        Communicate securely with your trading partner
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    {showChat ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              
              {/* Chat Content */}
              {showChat && (
                <div className="p-6 h-[600px] lg:h-[700px]">
                  <P2PChat orderId={orderId} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-4">Cancel Order</h3>
              <p className="text-white mb-4">
                Are you sure you want to cancel this order? Please provide a reason:
              </p>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Reason for cancellation..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white mb-4"
                rows={3}
              />
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Keep Order
                </button>
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Cancel Order
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default P2POrderDetail;
